// Adaptador de relay do Signal Protocol SDK sobre a API do Clicou, Guardou! (REST + WebSocket).
// Só trafega chaves públicas e envelopes cifrados; o servidor nunca vê texto em claro.
import { api, API, TOKEN_KEY } from "@/lib/api";

const unsupported = (what) => async () => { throw new Error(`Chat: ${what} não suportado.`); };

export class ApiRelay {
  constructor(deviceUid) {
    this.deviceUid = deviceUid;
    this._state = { state: "stopped", since: Date.now() };
    this._listeners = new Set();
    this._ws = null;
    this._onEnvelope = null;
    this._pollTimer = null;
    this.onEvent = null;
    this._deviceCache = new Map();
    this.localIdentity = null;
    this.onRemoteIdentity = null;
  }

  get relayConnectionState() { return this._state; }
  subscribeRelayConnectionState(l) { this._listeners.add(l); return () => this._listeners.delete(l); }
  _setState(state, reason) { this._state = { state, reason, since: Date.now() }; this._listeners.forEach((l) => l(this._state)); }

  async send(envelope) {
    const { data } = await api.post("/chat/relay/envelopes", { ...envelope, ciphertext: String(envelope.ciphertext) });
    return { messageId: data.messageId, serverTimestamp: data.serverTimestamp };
  }

  subscribe(userId, deviceId, onEnvelope) {
    this._onEnvelope = onEnvelope;
    this._stopped = false;
    this._connect();
    return () => { this._stopped = true; this._teardown(); this._setState("stopped", "closed"); };
  }

  _teardown() {
    if (this._ws) { try { this._ws.onclose = null; this._ws.close(); } catch { /* noop */ } this._ws = null; }
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
  }

  _connect() {
    if (this._stopped) return;
    this._setState("connecting");
    const url = API.replace(/^http/, "ws") + "/chat/ws";
    let ws;
    try { ws = new WebSocket(url); } catch { this._fallbackToPolling(); return; }
    this._ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: "auth", token: localStorage.getItem(TOKEN_KEY), device_uid: this.deviceUid }));
    ws.onmessage = async (ev) => {
      if (ev.data === "pong") return;
      let msg; try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === "ready") { this._setState("connected"); if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; } this._pingTimer = setInterval(() => { try { ws.send("ping"); } catch { /* noop */ } }, 25000); }
      else if (msg.type === "envelope") await this._deliver(msg.envelope);
      else if (msg.type === "event") this.onEvent?.(msg);
    };
    ws.onclose = (ev) => {
      this._ws = null;
      if (this._pingTimer) { clearInterval(this._pingTimer); this._pingTimer = null; }
      if (this._stopped) return;
      if (ev.code === 4001) { this._setState("stopped", "device-revoked"); this.onEvent?.({ type: "device_revoked" }); return; }
      this._setState("reconnecting", "closed");
      this._fallbackToPolling();
      setTimeout(() => this._connect(), 5000 + Math.random() * 3000);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
  }

  _fallbackToPolling() {
    if (this._pollTimer || this._stopped) return;
    const poll = async () => { try { const { data } = await api.get(`/chat/relay/envelopes/${this.deviceUid}`); for (const e of data) await this._deliver(e); } catch { /* offline */ } };
    poll();
    this._pollTimer = setInterval(poll, 4000);
  }

  async _deliver(envelope) {
    if (this._seen?.has(envelope.id)) return;
    (this._seen ||= new Set()).add(envelope.id);
    if (this._seen.size > 2000) this._seen = new Set([...this._seen].slice(-1000));
    try { await this._onEnvelope?.(envelope); } catch (e) { console.warn("Chat: envelope não processado", e?.message); }
  }

  async markDelivered(id) { await api.post(`/chat/relay/envelopes/${id}/delivered`); }

  async getDevices(userId) { return [{ deviceId: 1, registered: true, linked: false, enabled: true }]; }
  async registerDevice() { return 1; }
  async removeDevice() { /* dispositivos são gerenciados pela API /chat/devices */ }
  async getActiveDevices(userId) { return [{ userId, deviceId: 1 }]; }

  async provisionIdentityKey(req) { this.localIdentity = req.identity; await api.put("/chat/relay/identity", { device_uid: req.userId, identity: req.identity, registration_id: req.registrationId }); }
  async rotateIdentityKey(req) { return this.provisionIdentityKey(req); }
  async getIdentityKey(userId) {
    try { const { data } = await api.get(`/chat/relay/identity/${userId}`); if (data.user_id) this._deviceCache.set(userId, data.user_id); if (data.identity) this.onRemoteIdentity?.(userId, data.identity); return data.identity || null; }
    catch (e) { if (e?.response?.status === 404) return null; throw e; }
  }

  async uploadPreKeys(userId, deviceId, keys) { for (let i = 0; i < keys.length; i += 200) await api.post("/chat/relay/prekeys", { device_uid: userId, keys: keys.slice(i, i + 200) }); }
  async fetchPreKeyBundle(userId, deviceId) {
    let data;
    try { ({ data } = await api.post(`/chat/relay/prekeys/consume/${userId}`)); } catch (e) { if (e?.response?.status === 404) return null; throw e; }
    const signed = (t) => data.signedPreKeys.find((k) => k.type === t);
    const one = (t) => data.oneTimePreKeys.find((k) => k.type === t);
    const ec = signed("ecSignedPreKey"), kem = signed("kemLastResortPreKey"), ec1 = one("ecPreKey"), kem1 = one("kemOneTimePreKey");
    this.onRemoteIdentity?.(userId, data.identity);
    return {
      deviceId: 1, registrationId: data.registrationId, identity: data.identity,
      ecSignedPreKey: { keyId: ec.keyId, publicKey: ec.publicKey, signature: ec.signature },
      ecOneTimePreKey: ec1 ? { keyId: ec1.keyId, publicKey: ec1.publicKey } : null,
      kemLastResortPreKey: kem ? { keyId: kem.keyId, publicKey: kem.publicKey, signature: kem.signature } : null,
      kemOneTimePreKey: kem1 ? { keyId: kem1.keyId, publicKey: kem1.publicKey, signature: kem1.signature } : null,
    };
  }
  async getPreKeyInventory(userId) { const { data } = await api.get(`/chat/relay/prekeys/inventory/${userId}`); return data; }
  async getPreKeyCount(userId, _d, type) { const inv = await this.getPreKeyInventory(userId); return type === "ec" ? inv.ecOneTimePreKeyCount : inv.kemOneTimePreKeyCount; }
  async clearStaleKemPreKeys(userId) { const { data } = await api.delete(`/chat/relay/prekeys/stale/${userId}`); return data; }
  async publishPlannedPreKeys(userId, deviceId, plan) { const keys = await plan(await this.getPreKeyInventory(userId)); if (keys.length) await this.uploadPreKeys(userId, deviceId, keys); }
  async getEcSignedPreKeyMetadata(userId) { return (await this.getPreKeyInventory(userId)).ecSignedPreKey; }
  async getKemLastResortPreKeyMetadata(userId) { return (await this.getPreKeyInventory(userId)).kemLastResortPreKey; }

  createGroupState = unsupported("grupos"); getGroupState = unsupported("grupos"); getGroupJoinInfo = unsupported("grupos");
  getGroupChanges = unsupported("grupos"); submitGroupChange = unsupported("grupos"); issueAuthCredential = unsupported("credenciais");
}
